import PropTypes from 'prop-types';
import React from 'react';
import {
  TranslationProvider,
  InfoTooltip,
  utils,
  Spacer,
} from '@resy/ui-kitchen';

import GratuityButtons from '../GratuityButtons';

import './OrderSummary.scss';

class OrderSummary extends React.Component {
  static propTypes = {
    currencyCode: PropTypes.string,
    isUSLocation: PropTypes.bool,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        amount: PropTypes.number,
        is_included: PropTypes.bool,
        name: PropTypes.string,
        quantity: PropTypes.number,
      })
    ),
    locale: PropTypes.string,
    quantity: PropTypes.number,
    reservationCharge: PropTypes.number,
    reservationItemName: PropTypes.string,
    serviceFee: PropTypes.number,
    serviceCharge: PropTypes.shape({
      amount: PropTypes.number,
      value: PropTypes.string,
    }),
    serviceChargeChange: PropTypes.func,
    serviceChargeOptions: PropTypes.arrayOf(
      PropTypes.shape({
        amount: PropTypes.number,
        addOns: PropTypes.number,
        pricePerUnit: PropTypes.number,
        serviceFee: PropTypes.number,
        tax: PropTypes.number,
        total: PropTypes.number,
        value: PropTypes.string,
      })
    ),
    subtotal: PropTypes.number,
    tax: PropTypes.number,
    taxIncluded: PropTypes.bool,
    total: PropTypes.number,
  }

  static defaultProps = {
    currencyCode: undefined,
    isUSLocation: false,
    items: null,
    locale: undefined,
    quantity: null,
    reservationCharge: null,
    reservationItemName: null,
    serviceFee: null,
    serviceCharge: null,
    serviceChargeChange: () => {},
    serviceChargeOptions: null,
    subtotal: null,
    tax: null,
    taxIncluded: null,
    total: null,
  }

  getAmount = (amount) => {
    const {
      currencyCode, locale,
    } = this.props;

    return utils.getFormattedPrice(amount, currencyCode, '$', locale);
  }

  renderItem = (item) => (
    <div className={
      `OrderSummary__item ${item.category
        ? 'OrderSummary__item--category'
        : 'OrderSummary__item--addon'}`
}
    >
      <div>
        <span className="OrderSummary__item--quantity">
          {item.quantity}
        </span>
        {item.name}
      </div>
      <div>
        {item.is_included ? '—' : this.getAmount(item.amount)}
      </div>
    </div>
  );

  renderItems = () => {
    const { items } = this.props;

    if (!items || !items.length) {
      return null;
    }

    return items && items.map(this.renderItem);
  }

  renderServiceFee = () => {
    const { serviceFee } = this.props;

    if (!serviceFee) {
      return null;
    }

    return (
      <div className="OrderSummary__section--body__row">
        <span>
          <TranslationProvider translationKey="fees">
            <span>Service Fee</span>
          </TranslationProvider>
          <InfoTooltip content={this.renderServiceFeeTooltipContent()} ariaLabel="Service Fee info" />
        </span>
        <div>{this.getAmount(serviceFee)}</div>
      </div>
    );
  }

  renderServiceFeeTooltipContent = () => (
    <TranslationProvider translationKey="service_fee_tooltip">
      <span>
        Some jurisdictions require the restaurant to charge a Service Fee by law. A restaurant may also leverage this to cover costs of services rendered to the consumer.
      </span>
    </TranslationProvider>
  )

  renderReservationItem = () => {
    const { reservationCharge, reservationItemName, quantity } = this.props;

    return this.renderItem({
      category: true,
      amount: reservationCharge,
      name: reservationItemName,
      quantity,
    });
  }

  renderServiceCharge = () => {
    const { serviceCharge, serviceChargeOptions } = this.props;

    if (
      !serviceCharge?.amount
      && (!serviceChargeOptions || !serviceChargeOptions.length)
    ) {
      return null;
    }

    if (serviceChargeOptions?.length === 1 && serviceChargeOptions?.[0]?.amount === 0) {
      return null;
    }

    if (!serviceChargeOptions || (serviceChargeOptions?.length === 1)) {
      return (
        <div className="OrderSummary__section--body__row">
          <div>
            <TranslationProvider translationKey="gratuity">
              <span>Gratuity</span>
            </TranslationProvider>
            <span>
&nbsp;(
              {serviceCharge?.value}
              )
            </span>
          </div>
          <div>{this.getAmount(serviceCharge?.amount)}</div>
        </div>
      );
    }

    return (
      <div className="OrderSummary__section--body__row">
        <div id="add_gratuity">
          <TranslationProvider translationKey="add_gratuity">
            <span>Add Gratuity</span>
          </TranslationProvider>
        </div>
        <div>{this.getAmount(serviceCharge?.amount)}</div>
      </div>
    );
  }

  renderServiceChargeOptions = () => {
    const {
      currencyCode,
      isUSLocation,
      locale,
      serviceCharge,
      serviceChargeChange,
      serviceChargeOptions,
    } = this.props;

    if (!serviceChargeOptions || serviceChargeOptions.length <= 1) {
      return null;
    }

    return (
      <GratuityButtons
        currencyCode={currencyCode}
        handleGratuityChange={serviceChargeChange}
        locale={locale}
        options={serviceChargeOptions}
        selected={serviceCharge}
        labeledBy="add_gratuity"
        isUSLocation={isUSLocation}
      />
    );
  }

  renderSubTotal = () => {
    const { subtotal } = this.props;

    if (!subtotal) {
      return null;
    }

    return (
      <div className="OrderSummary__section--body__row">
        <TranslationProvider translationKey="subtotal">
          <div>Subtotal</div>
        </TranslationProvider>
        <div>{this.getAmount(subtotal)}</div>
      </div>
    );
  }

  renderTax = () => {
    const { tax, taxIncluded } = this.props;

    if (!tax || taxIncluded) {
      return null;
    }

    return (
      <div className="OrderSummary__section--body__row">
        <TranslationProvider translationKey="taxes">
          <span>Taxes</span>
        </TranslationProvider>
        <div>{this.getAmount(tax)}</div>
      </div>
    );
  }

  renderTaxIncluded = () => {
    const { subtotal } = this.props;

    if (!subtotal) {
      return null;
    }

    return (
      <div className="OrderSummary__section--body__row">
        <span className="OrderSummary__international-taxes">
          <TranslationProvider translationKey="subtotal_including_taxes">
            <span>Subtotal (including tax)</span>
          </TranslationProvider>
          <InfoTooltip content={this.renderTaxIncludedTooltipContent()} ariaLabel="Subtotal including tax info" />
        </span>
        <div>{this.getAmount(subtotal)}</div>
      </div>
    );
  }

  renderTaxIncludedTooltipContent = () => (
    <TranslationProvider translationKey="tax_included_tooltip">
      <span>
        All prices are tax inclusive and have been set by the venue. For an itemized invoice, please contact the restaurant.
      </span>
    </TranslationProvider>
  )

  renderTotal = () => {
    const { total } = this.props;

    if (!total) {
      return null;
    }

    return (
      <div className="OrderSummary__section--body__row OrderSummary__order-total">
        <div>
          <TranslationProvider translationKey="order_total">
            <span>Order Total</span>
          </TranslationProvider>
          :
        </div>
        <div>{this.getAmount(total)}</div>
      </div>
    );
  }

  render() {
    const { taxIncluded } = this.props;

    return (
      <div className="OrderSummary">
        <div className="OrderSummary__section">
          <div className="OrderSummary__section--body">
            {this.renderReservationItem()}

            {this.renderItems()}

            <Spacer height={15} />

            {taxIncluded
              ? this.renderTaxIncluded()
              : this.renderSubTotal()}

            {this.renderServiceCharge()}

            {this.renderServiceChargeOptions()}

            {this.renderTax()}

            {this.renderServiceFee()}

            {this.renderTotal()}
          </div>
        </div>
      </div>
    );
  }
}

OrderSummary.displayName = 'OrderSummary';
export default OrderSummary;
